import type { AppState, Message, Conversation, StreamingState, Model, Provider, Image, JobStatus, DraftInput, ActionStep, Reaction, ClientConfig } from './types';
import { Signal, batch } from './signal';

interface MessageUpdates {
    content?: string;
    id?: string;
}

const DEFAULT_CLIENT_CONFIG: ClientConfig = {
    reaction_snippet_words: 10,
    default_model: ''
};

export class Store {
    private conversations = new Signal<Conversation[]>([]);
    private currentConversationId = new Signal<string | null>(null);
    private messages = new Signal<Message[]>([]);
    private selectedModel = new Signal<string>('');
    private models = new Signal<Model[]>([]);
    private providers = new Signal<Provider[]>([]);
    private streamingStates = new Signal<Map<string, StreamingState>>(new Map());
    private activeJobs = new Signal<Map<string, { jobId: string; pollInterval: number }>>(new Map());
    private pendingImages = new Signal<Image[]>([]);
    private editingMessageId = new Signal<string | null>(null);
    private isAutoScrollEnabled = new Signal<boolean>(true);
    private userScrolledUp = new Signal<boolean>(false);
    private isSidebarOpen = new Signal<boolean>(false);
    private isDropZoneActive = new Signal<boolean>(false);
    private messageListVersion = new Signal<number>(0);
    private draftInputs = new Signal<Map<string, DraftInput>>(new Map());
    private isLoadingMessages = new Signal<boolean>(true);
    private clientConfig = new Signal<ClientConfig>(DEFAULT_CLIENT_CONFIG);
    
    private legacyListeners: Set<() => void> = new Set();

    constructor(initialState: Partial<AppState> = {}) {
        if (initialState.conversations) this.conversations.set(initialState.conversations);
        if (initialState.currentConversationId !== undefined) this.currentConversationId.set(initialState.currentConversationId);
        if (initialState.messages) this.messages.set(initialState.messages);
        if (initialState.selectedModel) this.selectedModel.set(initialState.selectedModel);
        if (initialState.models) this.models.set(initialState.models);
        if (initialState.streamingStates) this.streamingStates.set(initialState.streamingStates);
        if (initialState.activeJobs) this.activeJobs.set(initialState.activeJobs);
        if (initialState.pendingImages) this.pendingImages.set(initialState.pendingImages);
        if (initialState.editingMessageId !== undefined) this.editingMessageId.set(initialState.editingMessageId);
        if (initialState.isAutoScrollEnabled !== undefined) this.isAutoScrollEnabled.set(initialState.isAutoScrollEnabled);
        if (initialState.userScrolledUp !== undefined) this.userScrolledUp.set(initialState.userScrolledUp);
        if (initialState.isSidebarOpen !== undefined) this.isSidebarOpen.set(initialState.isSidebarOpen);
        if (initialState.isDropZoneActive !== undefined) this.isDropZoneActive.set(initialState.isDropZoneActive);
        if (initialState.messageListVersion !== undefined) this.messageListVersion.set(initialState.messageListVersion);
        if (initialState.draftInputs) this.draftInputs.set(initialState.draftInputs);
    }

    getState(): AppState {
        return {
            conversations: this.conversations.get(),
            currentConversationId: this.currentConversationId.get(),
            messages: this.messages.get(),
            selectedModel: this.selectedModel.get(),
            models: this.models.get(),
            streamingStates: this.streamingStates.get(),
            activeJobs: this.activeJobs.get(),
            pendingImages: this.pendingImages.get(),
            editingMessageId: this.editingMessageId.get(),
            isAutoScrollEnabled: this.isAutoScrollEnabled.get(),
            userScrolledUp: this.userScrolledUp.get(),
            isSidebarOpen: this.isSidebarOpen.get(),
            isDropZoneActive: this.isDropZoneActive.get(),
            messageListVersion: this.messageListVersion.get(),
            draftInputs: this.draftInputs.get(),
        };
    }

    subscribe(listener: () => void): () => void {
        this.legacyListeners.add(listener);
        return () => this.legacyListeners.delete(listener);
    }

    private notifyLegacy(): void {
        this.legacyListeners.forEach(listener => listener());
    }

    private incrementMessageListVersion(silent: boolean = false): void {
        this.messageListVersion.update(v => v + 1);
        if (!silent) {
            this.notifyLegacy();
        }
    }

    getConversations(): Conversation[] {
        return this.conversations.get();
    }

    subscribeToConversations(fn: (val: Conversation[]) => void): () => void {
        return this.conversations.subscribe(fn);
    }

    setConversations(conversations: Conversation[]): void {
        this.conversations.set(conversations);
        this.notifyLegacy();
    }

    addConversation(conversation: Conversation): void {
        this.conversations.update(list => [conversation, ...list]);
        this.notifyLegacy();
    }

    deleteConversation(id: string): void {
        batch(() => {
            this.conversations.update(list => list.filter(c => c.id !== id));
            if (this.currentConversationId.get() === id) {
                this.currentConversationId.set(null);
                this.messages.set([]);
            }
        });
        this.notifyLegacy();
    }

    getCurrentConversationId(): string | null {
        return this.currentConversationId.get();
    }

    subscribeToCurrentConversationId(fn: (val: string | null) => void): () => void {
        return this.currentConversationId.subscribe(fn);
    }

    setCurrentConversation(conversationId: string | null): void {
        batch(() => {
            this.currentConversationId.set(conversationId);
            this.messages.set([]);
            this.isLoadingMessages.set(true);
        });
        this.incrementMessageListVersion();
    }

    updateConversationModel(conversationId: string, model: string): void {
        this.conversations.update(list =>
            list.map(c => c.id === conversationId ? { ...c, model } : c)
        );
        this.notifyLegacy();
    }

    updateConversationTitle(conversationId: string, title: string): void {
        this.conversations.update(list =>
            list.map(c => c.id === conversationId ? { ...c, title } : c)
        );
        this.notifyLegacy();
    }

    moveConversationToTop(conversationId: string): void {
        this.conversations.update(list => {
            const index = list.findIndex(c => c.id === conversationId);
            if (index > 0) {
                const conversation = list[index];
                return [conversation, ...list.filter(c => c.id !== conversationId)];
            }
            return list;
        });
        this.notifyLegacy();
    }

    getMessages(): Message[] {
        return this.messages.get();
    }

    getVisibleMessages(): Message[] {
        return this.messages.get().filter(m => !m.flags?.includes('hidden'));
    }

    subscribeToMessages(fn: (val: Message[]) => void): () => void {
        return this.messages.subscribe(fn);
    }

    getMessageListVersion(): number {
        return this.messageListVersion.get();
    }

    subscribeToMessageListVersion(fn: (val: number) => void): () => void {
        return this.messageListVersion.subscribe(fn);
    }

    setMessages(messages: Message[]): void {
        console.log('[setMessages] Setting', messages.length, 'messages');
        this.messages.set(messages);
        this.incrementMessageListVersion();
    }

    addMessage(message: Message, silent: boolean = false): void {
        this.messages.update(list => [...list, message]);
        if (!silent) {
            this.notifyLegacy();
        }
    }

    updateMessage(messageId: string, updates: MessageUpdates, silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                newList[index] = { ...newList[index], ...updates };
                return newList;
            }
            return list;
        });
        if (!silent) {
            this.notifyLegacy();
        }
    }

    updateMessageActionSteps(messageId: string, actionSteps: ActionStep[], silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                newList[index] = { ...newList[index], actionSteps };
                return newList;
            }
            return list;
        });
        if (!silent) {
            this.notifyLegacy();
        }
    }

    updateMessageReactions(messageId: string, reactions: Reaction[], silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                newList[index] = { ...newList[index], reactions };
                return newList;
            }
            return list;
        });
        this.incrementMessageListVersion(silent);
    }

    updateMessageFlags(messageId: string, flags: string[], silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                newList[index] = { ...newList[index], flags };
                return newList;
            }
            return list;
        });
        this.incrementMessageListVersion(silent);
    }

    addReaction(messageId: string, emoji: string, fromRole: 'user' | 'assistant', silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                const existing = newList[index].reactions || [];
                const filtered = existing.filter(r => r.from_role !== fromRole);
                newList[index] = { 
                    ...newList[index], 
                    reactions: [...filtered, { emoji, from_role: fromRole }]
                };
                return newList;
            }
            return list;
        });
        this.incrementMessageListVersion(silent);
    }

    removeReaction(messageId: string, fromRole: 'user' | 'assistant', silent: boolean = false): void {
        this.messages.update(list => {
            const index = list.findIndex(m => m.id === messageId);
            if (index !== -1) {
                const newList = [...list];
                const existing = newList[index].reactions || [];
                const filtered = existing.filter(r => r.from_role !== fromRole);
                newList[index] = { ...newList[index], reactions: filtered.length > 0 ? filtered : undefined };
                return newList;
            }
            return list;
        });
        this.incrementMessageListVersion(silent);
    }

    deleteMessagesAfter(messageIndex: number, silent: boolean = false): void {
        console.log('[deleteMessagesAfter] Index:', messageIndex, 'silent:', silent, 'count before:', this.messages.get().length);
        this.messages.update(list => list.slice(0, messageIndex + 1));
        console.log('[deleteMessagesAfter] Count after:', this.messages.get().length);
        this.incrementMessageListVersion(silent);
    }

    removeMessage(messageId: string, silent: boolean = false): void {
        this.messages.update(list => list.filter(m => m.id !== messageId));
        if (!silent) {
            this.notifyLegacy();
        }
    }

    getStreamingState(conversationId: string | null): StreamingState {
        if (!conversationId) {
            return { isStreaming: false, hasReceivedFirstToken: false };
        }
        return this.streamingStates.get().get(conversationId) || { isStreaming: false, hasReceivedFirstToken: false };
    }

    subscribeToStreamingStates(fn: (val: Map<string, StreamingState>) => void): () => void {
        return this.streamingStates.subscribe(fn);
    }

    startStreaming(conversationId: string, silent: boolean = false): void {
        this.streamingStates.update(map => {
            const newMap = new Map(map);
            newMap.set(conversationId, {
                isStreaming: true,
                hasReceivedFirstToken: false,
            });
            return newMap;
        });
        if (!silent) {
            this.notifyLegacy();
        }
    }

    updateStreamingState(conversationId: string, state: Partial<StreamingState>, silent: boolean = false): void {
        this.streamingStates.update(map => {
            const current = map.get(conversationId);
            if (current) {
                const newMap = new Map(map);
                newMap.set(conversationId, { ...current, ...state });
                return newMap;
            }
            return map;
        });
        if (!silent) {
            this.notifyLegacy();
        }
    }

    stopStreaming(conversationId: string, silent: boolean = false): void {
        this.streamingStates.update(map => {
            const newMap = new Map(map);
            newMap.delete(conversationId);
            return newMap;
        });
        if (!silent) {
            this.notifyLegacy();
        }
    }

    getPendingImages(): Image[] {
        return this.pendingImages.get();
    }

    subscribeToPendingImages(fn: (val: Image[]) => void): () => void {
        return this.pendingImages.subscribe(fn);
    }

    setPendingImages(images: Image[]): void {
        this.pendingImages.set(images);
        this.notifyLegacy();
    }

    addPendingImage(image: Image): void {
        this.pendingImages.update(list => [...list, image]);
        this.notifyLegacy();
    }

    removePendingImage(index: number): void {
        this.pendingImages.update(list => {
            const newList = [...list];
            newList.splice(index, 1);
            return newList;
        });
        this.notifyLegacy();
    }

    clearPendingImages(): void {
        this.pendingImages.set([]);
        this.notifyLegacy();
    }

    getImageSessionSize(): number {
        let totalSize = 0;
        const messages = this.messages.get();
        for (const message of messages) {
            if (message.images) {
                for (const image of message.images) {
                    totalSize += Math.ceil(image.data.length * 3 / 4);
                }
            }
        }
        const pending = this.pendingImages.get();
        for (const image of pending) {
            totalSize += Math.ceil(image.data.length * 3 / 4);
        }
        return totalSize;
    }

    getImageBudgetRemaining(): number {
        const SESSION_IMAGE_BUDGET = 10 * 1024 * 1024;
        return Math.max(0, SESSION_IMAGE_BUDGET - this.getImageSessionSize());
    }

    getImageCount(): number {
        let count = 0;
        const messages = this.messages.get();
        for (const message of messages) {
            if (message.images) {
                count += message.images.length;
            }
        }
        count += this.pendingImages.get().length;
        return count;
    }

    getSelectedModel(): string {
        return this.selectedModel.get();
    }

    subscribeToSelectedModel(fn: (val: string) => void): () => void {
        return this.selectedModel.subscribe(fn);
    }

    setSelectedModel(model: string): void {
        this.selectedModel.set(model);
        this.notifyLegacy();
    }

    getModels(): Model[] {
        return this.models.get();
    }

    subscribeToModels(fn: (val: Model[]) => void): () => void {
        return this.models.subscribe(fn);
    }

    setModels(models: Model[]): void {
        batch(() => {
            this.models.set(models);
            if (!this.selectedModel.get() && models.length > 0) {
                const defaultId = this.clientConfig.get().default_model;
                const defaultModel = models.find(m => m.id === defaultId);
                this.selectedModel.set(defaultModel?.id ?? models[0].id);
            }
        });
        this.notifyLegacy();
    }

    getProviders(): Provider[] {
        return this.providers.get();
    }

    subscribeToProviders(fn: (val: Provider[]) => void): () => void {
        return this.providers.subscribe(fn);
    }

    setProviders(providers: Provider[]): void {
        this.providers.set(providers);
        this.notifyLegacy();
    }

    getDefaultModel(): string {
        return this.clientConfig.get().default_model;
    }

    setDefaultModel(modelId: string): void {
        this.clientConfig.update(config => ({ ...config, default_model: modelId }));
        this.notifyLegacy();
    }

    getEditingMessageId(): string | null {
        return this.editingMessageId.get();
    }

    subscribeToEditingMessageId(fn: (val: string | null) => void): () => void {
        return this.editingMessageId.subscribe(fn);
    }

    setEditingMessage(messageId: string | null): void {
        this.editingMessageId.set(messageId);
        this.notifyLegacy();
    }

    isAutoScrollEnabledValue(): boolean {
        return this.isAutoScrollEnabled.get();
    }

    subscribeToAutoScroll(fn: (val: boolean) => void): () => void {
        return this.isAutoScrollEnabled.subscribe(fn);
    }

    setAutoScroll(enabled: boolean): void {
        this.isAutoScrollEnabled.set(enabled);
        this.notifyLegacy();
    }

    getUserScrolledUp(): boolean {
        return this.userScrolledUp.get();
    }

    subscribeToUserScrolledUp(fn: (val: boolean) => void): () => void {
        return this.userScrolledUp.subscribe(fn);
    }

    setUserScrolledUp(scrolled: boolean): void {
        this.userScrolledUp.set(scrolled);
        this.notifyLegacy();
    }

    isSidebarOpenValue(): boolean {
        return this.isSidebarOpen.get();
    }

    subscribeToSidebarOpen(fn: (val: boolean) => void): () => void {
        return this.isSidebarOpen.subscribe(fn);
    }

    setSidebarOpen(isOpen: boolean): void {
        this.isSidebarOpen.set(isOpen);
        this.notifyLegacy();
    }

    isDropZoneActiveValue(): boolean {
        return this.isDropZoneActive.get();
    }

    subscribeToDropZoneActive(fn: (val: boolean) => void): () => void {
        return this.isDropZoneActive.subscribe(fn);
    }

    setDropZoneActive(isActive: boolean): void {
        this.isDropZoneActive.set(isActive);
        this.notifyLegacy();
    }

    setActiveJob(conversationId: string, jobId: string, api: any): void {
        const pollInterval = setInterval(async () => {
            try {
                const status = await api.getJobStatus(conversationId, jobId);
                if (status.status === 'completed' || status.status === 'cancelled' || status.status === 'error') {
                    this.clearActiveJob(conversationId);
                }
            } catch (error) {
                console.error('Error polling job status:', error);
            }
        }, 2000) as unknown as number;

        this.activeJobs.update(map => {
            const newMap = new Map(map);
            newMap.set(conversationId, { jobId, pollInterval });
            return newMap;
        });
        this.notifyLegacy();
    }

    clearActiveJob(conversationId: string): void {
        const job = this.activeJobs.get().get(conversationId);
        if (job) {
            clearInterval(job.pollInterval);
            this.activeJobs.update(map => {
                const newMap = new Map(map);
                newMap.delete(conversationId);
                return newMap;
            });
            this.notifyLegacy();
        }
    }

    isJobActive(conversationId: string): boolean {
        return this.activeJobs.get().has(conversationId);
    }

    saveDraftInput(conversationId: string, text: string, images: Image[]): void {
        if (!conversationId) return;
        this.draftInputs.update(map => {
            const newMap = new Map(map);
            newMap.set(conversationId, { text, images });
            return newMap;
        });
    }

    getDraftInput(conversationId: string | null): DraftInput | null {
        if (!conversationId) return null;
        return this.draftInputs.get().get(conversationId) || null;
    }

    clearDraftInput(conversationId: string | null): void {
        if (!conversationId) return;
        this.draftInputs.update(map => {
            const newMap = new Map(map);
            newMap.delete(conversationId);
            return newMap;
        });
    }

    isLoadingMessagesValue(): boolean {
        return this.isLoadingMessages.get();
    }

    setLoadingMessages(loading: boolean): void {
        this.isLoadingMessages.set(loading);
        this.incrementMessageListVersion();
    }

    getClientConfig(): ClientConfig {
        return this.clientConfig.get();
    }

    setClientConfig(config: ClientConfig): void {
        this.clientConfig.set(config);
    }

    getReactionSnippetWords(): number {
        return this.clientConfig.get().reaction_snippet_words;
    }
}